import { useState, useCallback, useEffect } from "react";
import {
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import { convertUTCToLocal } from "@/lib/utils/dateUtils";
import {
  AddLocationAlt as AddLocationAltIcon,
  Delete as DeleteIcon,
  Map as MapIcon,
  LocationOn as LocationOnIcon,
  Close as CloseIcon,
  Public as PublicIcon,
  Explore as ExploreIcon,
} from "@mui/icons-material";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import NewDeleteModal from "@components/newDeleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { Map as GoogleMap } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import OrganizationHeader from "./components/OrganizationHeader";
import SitesGuideDialog from "@/components/dialogs/SitesGuideDialog";

// Map styling
const mapContainerStyle = {
  width: "100%",
  height: "400px",
};
const defaultCenter = {
  lat: 0,
  lng: 0,
};
const londonCenter = {
  lat: 51.5074,
  lng: -0.1278,
};

function Sites() {
  const { t } = useTranslation();
  // Define all hooks at the top
  const theme = useTheme();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const queryClient = useQueryClient();
  const [loadError, setLoadError] = useState(null);
  const [newSiteName, setNewSiteName] = useState("");
  const [newChildSiteName, setNewChildSiteName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [parentSiteId, setParentSiteId] = useState(null);
  const [parentName, setParentName] = useState("");
  const [location, setLocation] = useState(defaultCenter);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [viewLocationDialogOpen, setViewLocationDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [allSitesDialogOpen, setAllSitesDialogOpen] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const { mutateAsync: postSite, isLoading: postIsLoading } = usePostJsonData();
  const { mutateAsync: deleteSite } = useDeleteData("/api/v1/org-admin/sites", [
    "sites",
  ]);
  const {
    data: sites,
    isLoading: sitesLoading,
    error: errorSite,
  } = useAuthenticatedQuery(["sites"], "/api/v1/metadata/sites");

  // Add API key check with debug logging
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!apiKey) {
      console.error(
        "Google Maps API key is missing. Please check your .env file."
      );
      setToast({
        open: true,
        message:
          t("sites.errorMissingApiKey") ||
          "Google Maps API key is missing. Please check your configuration.",
        severity: "error",
      });
    }
  }, [apiKey, setToast, t]);

  // Load Google Maps API via APIProvider
  const onMapClick = useCallback((event) => {
    const { lat, lng } = event.detail.latLng;

    setLocation({ lat, lng });
  }, []);

  // Fit bounds when dialog opens and map is ready
  useEffect(() => {
    if (
      allSitesDialogOpen &&
      mapInstance &&
      mapsLoaded &&
      window.google &&
      window.google.maps &&
      sites &&
      sites.length > 0
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasValidSites = false;

      sites.forEach((site) => {
        if (
          Number.isFinite(site.latitude) &&
          Number.isFinite(site.longitude) &&
          site.latitude !== 0 &&
          site.longitude !== 0
        ) {
          bounds.extend({ lat: site.latitude, lng: site.longitude });
          hasValidSites = true;
        }
      });

      if (hasValidSites && !bounds.isEmpty()) {
        // Fit bounds with padding - this allows manual zoom after
        mapInstance.fitBounds(bounds, { padding: 80 });
      }
    }
  }, [allSitesDialogOpen, mapInstance, mapsLoaded, sites]);

  // Early returns after all hooks
  if (sitesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} aria-label={t("sites.loadingAria")} />
          <Typography variant="body1" color="text.secondary">
            {t("sites.loadingMessage")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (errorSite) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t("sites.errorLoading")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("sites.errorMessage")}
        </Typography>
      </Box>
    );
  }

  if (!apiKey) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t("sites.errorMissingApiKey") || "Google Maps API key is missing"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("sites.errorCheckEnv") ||
            "Please check your environment configuration."}
        </Typography>
      </Box>
    );
  }

  const addItem = (newItem, endpoint, parent = null, lat, lng) => {
    if (newItem.trim() === "" || !Number.isFinite(lat) || !Number.isFinite(lng))
      return;

    const newObject = {
      name: newItem,
      parent_id: parent,
      latitude: lat,
      longitude: lng,
    };

    postSite(
      { url: endpoint, data: newObject },
      {
        onSuccess: () => {
          setNewSiteName("");
          setNewChildSiteName("");
          setLocation(defaultCenter);
          setToast({
            open: true,
            message: t("sites.successAddToast"),
            severity: "success",
          });
          queryClient.invalidateQueries(["sites"]);
        },
        onError: (err) => {
          handleApiError(err);
        },
      }
    );
    handleCloseDialog();
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteSite(itemToDelete);
      setToast({
        open: true,
        message: t("sites.successDeleteToast"),
        severity: "success",
      });
    } catch (err) {
      handleApiError(err);
    } finally {
      setItemToDelete(null);
      setConfirmOpen(false);
    }
  };

  const buildSiteTree = (sites) => {
    const siteMap = new Map();
    const roots = [];

    sites.forEach((site) => {
      siteMap.set(site.id, { ...site, children: [] });
    });

    sites.forEach((site) => {
      if (site.parent_id) {
        siteMap.get(site.parent_id).children.push(siteMap.get(site.id));
      } else {
        roots.push(siteMap.get(site.id));
      }
    });

    return roots;
  };

  const siteTree = buildSiteTree(sites);

  const handleViewLocation = (site) => {
    setSelectedSite(site);
    setViewLocationDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewChildSiteName("");
    setLocation(defaultCenter);
  };

  const handleCloseViewLocationDialog = () => {
    setViewLocationDialogOpen(false);
    setSelectedSite(null);
  };

  const handleOpenDialog = (siteId, parent) => {
    setParentName(parent || t("sites.newSite"));
    setParentSiteId(siteId);

    let initialLocation = londonCenter;

    if (sites && sites.length > 0) {
      if (siteId) {
        const parentSite = sites.find((site) => site.id === siteId);
        if (
          parentSite &&
          Number.isFinite(parentSite.latitude) &&
          Number.isFinite(parentSite.longitude) &&
          parentSite.latitude !== 0 &&
          parentSite.longitude !== 0
        ) {
          initialLocation = {
            lat: parentSite.latitude,
            lng: parentSite.longitude,
          };
        }
      } else {
        const firstSite = sites[0];
        if (
          Number.isFinite(firstSite.latitude) &&
          Number.isFinite(firstSite.longitude) &&
          firstSite.latitude !== 0 &&
          firstSite.longitude !== 0
        ) {
          initialLocation = {
            lat: firstSite.latitude,
            lng: firstSite.longitude,
          };
        }
      }
    }

    setLocation(initialLocation);
    setDialogOpen(true);
  };

  const handleOpenAllSitesDialog = () => {
    setAllSitesDialogOpen(true);
  };

  const handleCloseAllSitesDialog = () => {
    setAllSitesDialogOpen(false);
    setSelectedMarkerId(null);
    setMapInstance(null);
  };

  const handleMarkerClick = (siteId) => {
    setSelectedMarkerId((prev) => (prev === siteId ? null : siteId));
  };

  const handleInfoWindowClose = () => {
    setSelectedMarkerId(null);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && newSiteName.trim() !== "") {
      handleOpenDialog(null, t("sites.newSite"));
    }
  };

  const renderSiteTree = (tree, depth = 0) =>
    tree.map((node) => (
      <Box
        key={node.id}
        sx={{
          ml: depth * 3,
          mb: 1.5,
        }}
      >
        <Card
          elevation={depth === 0 ? 3 : 1}
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: depth === 0 ? "primary.main" : "divider",
            transition: "all 0.3s ease",
            background:
              depth === 0
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[depth === 0 ? 8 : 4],
              borderColor: depth === 0 ? "primary.main" : "secondary.main",
              "& .site-actions": {
                opacity: 1,
              },
            },
          }}
        >
          <ListItem sx={{ p: 2.5 }}>
            <ListItemAvatar>
              <Avatar
                sx={{
                  bgcolor:
                    depth === 0
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.secondary.main, 0.1),
                  border: "2px solid",
                  borderColor:
                    depth === 0
                      ? alpha(theme.palette.primary.main, 0.2)
                      : alpha(theme.palette.secondary.main, 0.2),
                  width: depth === 0 ? 56 : 48,
                  height: depth === 0 ? 56 : 48,
                  mr: 2,
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: depth === 0 ? "primary.main" : "secondary.main",
                    fontSize: depth === 0 ? 28 : 24,
                  }}
                />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography
                  variant={depth === 0 ? "h6" : "subtitle1"}
                  fontWeight={depth === 0 ? "bold" : "600"}
                  color="text.primary"
                >
                  {node.name}
                </Typography>
              }
            />
            <Box
              className="site-actions"
              sx={{
                opacity: 0.7,
                transition: "opacity 0.3s ease",
                display: "flex",
                gap: 1,
              }}
            >
              <Tooltip title={t("sites.viewLocationTooltip")}>
                <IconButton
                  color="info"
                  onClick={() => handleViewLocation(node)}
                  sx={{
                    borderRadius: 2,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                    },
                  }}
                  aria-label={t("sites.viewLocationAria", { name: node.name })}
                >
                  <MapIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("sites.addSubsiteTooltip")}>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDialog(node.id, node.name)}
                  sx={{
                    borderRadius: 2,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                  aria-label={t("sites.addSubsiteAria", { name: node.name })}
                >
                  <AddLocationAltIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("sites.deleteSiteTooltip")}>
                <IconButton
                  color="error"
                  onClick={() => confirmDelete(node.id)}
                  sx={{
                    borderRadius: 2,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                  aria-label={t("sites.deleteSiteAria", { name: node.name })}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </ListItem>
        </Card>
        {node.children && node.children.length > 0 && (
          <Box sx={{ mt: 1 }}>{renderSiteTree(node.children, depth + 1)}</Box>
        )}
      </Box>
    ));

  const getMapCenterAndBounds = () => {
    // If no sites, return default
    if (!sites || sites.length === 0) {
      return { center: londonCenter, zoom: 14 };
    }

    // If maps not loaded yet, we'll let the useEffect handle bounds fitting
    // Just return a default center for now
    if (
      !mapsLoaded ||
      !window.google ||
      !window.google.maps ||
      typeof window.google.maps.LatLngBounds !== "function"
    ) {
      // Return center of first valid site or default
      const firstValidSite = sites.find(
        (site) =>
          Number.isFinite(site.latitude) &&
          Number.isFinite(site.longitude) &&
          site.latitude !== 0 &&
          site.longitude !== 0
      );

      if (firstValidSite) {
        return {
          center: {
            lat: firstValidSite.latitude,
            lng: firstValidSite.longitude,
          },
          zoom: 10,
        };
      }

      return { center: londonCenter, zoom: 14 };
    }

    try {
      const bounds = new window.google.maps.LatLngBounds();
      let hasValidSites = false;

      sites.forEach((site) => {
        if (
          Number.isFinite(site.latitude) &&
          Number.isFinite(site.longitude) &&
          site.latitude !== 0 &&
          site.longitude !== 0
        ) {
          bounds.extend({ lat: site.latitude, lng: site.longitude });
          hasValidSites = true;
        }
      });

      // If no valid sites, return default
      if (!hasValidSites || bounds.isEmpty()) {
        return { center: londonCenter, zoom: 14 };
      }

      const centerPt = bounds.getCenter();

      // Return the calculated center
      return {
        center: { lat: centerPt.lat(), lng: centerPt.lng() },
        zoom: undefined, // Let fitBounds in useEffect handle the zoom
      };
    } catch (error) {
      // If any error occurs, return first valid site or default
      console.warn("Error calculating map bounds:", error);
      const firstValidSite = sites.find(
        (site) =>
          Number.isFinite(site.latitude) &&
          Number.isFinite(site.longitude) &&
          site.latitude !== 0 &&
          site.longitude !== 0
      );

      if (firstValidSite) {
        return {
          center: {
            lat: firstValidSite.latitude,
            lng: firstValidSite.longitude,
          },
          zoom: 10,
        };
      }

      return { center: londonCenter, zoom: 14 };
    }
  };

  const { center, zoom } = getMapCenterAndBounds();

  return (
    <APIProvider
      apiKey={apiKey}
      onLoad={() => {
        setMapsLoaded(true);
      }}
      onError={(err) => {
        console.error("Google Maps API error:", err);
        console.error("API Key that caused error:", apiKey);
        setLoadError(err.message);
        setToast({
          open: true,
          message: `${t("sites.errorLoadingMaps")} Error: ${err.message}`,
          severity: "error",
        });
      }}
    >
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
        {/* Compact Enterprise Header */}
        <OrganizationHeader
          icon={PublicIcon}
          title={t("sites.headerTitle")}
          subtitle={t("sites.headerSubtitle")}
          count={sites?.length || 0}
          onHelpClick={() => setHelpModalOpen(true)}
          helpTooltip={t("sites.helpTooltip")}
          helpAriaLabel={t("sites.helpButtonAria")}
        />

        <Box sx={{ px: 4, pb: 4 }}>
          {/* Add New Site Section */}
          <Card
            elevation={3}
            sx={{
              mb: 4,
              overflow: "visible",
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 3, color: "text.primary", fontWeight: 600 }}
              >
                {t("sites.addSectionTitle")}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label={t("sites.newSiteLabel")}
                  size="small"
                  variant="outlined"
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: theme.shadows[2],
                      },
                      "&.Mui-focused": {
                        boxShadow: theme.shadows[4],
                      },
                    },
                  }}
                  aria-label={t("sites.newSiteAria")}
                />
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={() => handleOpenDialog(null, t("sites.newSite"))}
                  disabled={postIsLoading || newSiteName.trim() === ""}
                  startIcon={
                    postIsLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <LocationOnIcon />
                    )
                  }
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 140,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                      boxShadow: theme.shadows[8],
                      transform: "translateY(-2px)",
                    },
                    "&.Mui-disabled": {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.6)} 0%, ${alpha(theme.palette.grey[300], 0.4)} 100%)`,
                      color: alpha(theme.palette.text.disabled, 0.7),
                      boxShadow: "none",
                      transform: "none",
                    },
                    transition: "all 0.3s ease",
                  }}
                  aria-label={t("sites.addButtonAria")}
                >
                  {t("sites.addButton")}
                </Button>
              </Stack>
            </Box>
          </Card>

          {/* Site List Section */}
          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ExploreIcon sx={{ color: "info.main", fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="text.primary"
                  >
                    {t("sites.listTitle")}
                  </Typography>
                </Stack>
                <Tooltip title={t("sites.viewAllSitesOnMap")}>
                  <Button
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={handleOpenAllSitesDialog}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                      },
                    }}
                  >
                    {t("sites.viewAllSitesButton")}
                  </Button>
                </Tooltip>
              </Stack>
            </Box>

            <Box sx={{ p: 3 }}>
              {siteTree.length > 0 ? (
                <Box sx={{ "& > *:last-child": { mb: 0 } }}>
                  {renderSiteTree(siteTree)}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      p: 3,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      mb: 3,
                    }}
                  >
                    <LocationOnIcon
                      sx={{
                        fontSize: 48,
                        color: "info.main",
                        opacity: 0.7,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                    fontWeight="500"
                  >
                    {t("sites.noSites")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("sites.noSitesDesc")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Box>

        {/* Add Site/Subsite Dialog with Google Map */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          sx={{ "& .MuiDialog-paper": { width: "600px" } }}
          aria-labelledby="add-site-dialog-title"
        >
          <DialogTitle id="add-site-dialog-title">
            {t("sites.addDialogTitle", {
              type: parentSiteId ? t("sites.subsite") : t("sites.site"),
              parentName,
            })}
            <IconButton
              onClick={handleCloseDialog}
              sx={{ position: "absolute", right: 8, top: 8 }}
              aria-label={t("sites.closeDialogAria")}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              label={t("sites.siteNameLabel")}
              variant="outlined"
              value={parentSiteId ? newChildSiteName : newSiteName}
              onChange={(e) =>
                parentSiteId
                  ? setNewChildSiteName(e.target.value)
                  : setNewSiteName(e.target.value)
              }
              fullWidth
              sx={{ mt: 2, mb: 2 }}
              aria-label={t("sites.siteNameAria")}
            />
            {loadError ? (
              <Typography color="error">{t("sites.errorLoadMap")}</Typography>
            ) : mapsLoaded ? (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    textAlign: "center",
                  }}
                >
                  📍 {t("sites.clickMapInstruction")}
                </Typography>
                <GoogleMap
                  style={mapContainerStyle}
                  defaultCenter={location}
                  defaultZoom={14}
                  mapId="YOUR_MAP_ID"
                  zoomControl
                  disableDefaultUI={false}
                  minZoom={3}
                  maxZoom={18}
                  onClick={onMapClick}
                  aria-label={t("sites.mapAria")}
                >
                  {Number.isFinite(location.lat) &&
                    Number.isFinite(location.lng) &&
                    location.lat !== 0 &&
                    location.lng !== 0 && (
                      <AdvancedMarker position={location} />
                    )}
                </GoogleMap>
              </>
            ) : (
              <CircularProgress aria-label={t("sites.loadingMapAria")} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {t("sites.cancelButton")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                postIsLoading ||
                (parentSiteId ? newChildSiteName : newSiteName).trim() === "" ||
                location.lat === 0 ||
                location.lng === 0
              }
              onClick={() =>
                addItem(
                  parentSiteId ? newChildSiteName : newSiteName,
                  "/api/v1/org-admin/sites",
                  parentSiteId,
                  location.lat,
                  location.lng
                )
              }
              aria-label={t("sites.addSiteButtonAria", {
                type: parentSiteId ? t("sites.subsite") : t("sites.site"),
              })}
            >
              {t("sites.addSiteButton", {
                type: parentSiteId ? t("sites.subsite") : t("sites.site"),
              })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Site Location Dialog */}
        <Dialog
          open={viewLocationDialogOpen}
          onClose={handleCloseViewLocationDialog}
          sx={{ "& .MuiDialog-paper": { width: "600px" } }}
          aria-labelledby="view-location-dialog-title"
        >
          <DialogTitle id="view-location-dialog-title">
            {t("sites.viewLocationDialogTitle", { name: selectedSite?.name })}
            <IconButton
              onClick={handleCloseViewLocationDialog}
              sx={{ position: "absolute", right: 8, top: 8 }}
              aria-label={t("sites.closeViewLocationAria")}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {loadError ? (
              <Typography color="error">{t("sites.errorLoadMap")}</Typography>
            ) : mapsLoaded && selectedSite ? (
              Number.isFinite(selectedSite.latitude) &&
              Number.isFinite(selectedSite.longitude) &&
              selectedSite.latitude !== 0 &&
              selectedSite.longitude !== 0 ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t("sites.coordinates", {
                      lat: selectedSite.latitude.toFixed(6),
                      lng: selectedSite.longitude.toFixed(6),
                    })}
                  </Typography>
                  <GoogleMap
                    defaultCenter={{
                      lat: selectedSite.latitude,
                      lng: selectedSite.longitude,
                    }}
                    style={mapContainerStyle}
                    defaultZoom={14}
                    mapId="YOUR_MAP_ID"
                    zoomControl
                    disableDefaultUI={false}
                    minZoom={3}
                    maxZoom={18}
                    aria-label={t("sites.locationMapAria", {
                      name: selectedSite.name,
                    })}
                  >
                    <AdvancedMarker
                      position={{
                        lat: selectedSite.latitude,
                        lng: selectedSite.longitude,
                      }}
                    />
                  </GoogleMap>
                </>
              ) : (
                <Typography color="error">
                  {t("sites.errorInvalidCoordinates")}
                </Typography>
              )
            ) : (
              <CircularProgress aria-label={t("sites.loadingMapAria")} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseViewLocationDialog}>
              {t("sites.closeButton")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* All Sites Map Dialog - Full Screen */}
        <Dialog
          open={allSitesDialogOpen}
          onClose={handleCloseAllSitesDialog}
          fullScreen
          sx={{
            "& .MuiDialog-paper": {
              bgcolor: "background.default",
            },
          }}
          aria-labelledby="all-sites-map-dialog-title"
        >
          <DialogTitle
            id="all-sites-map-dialog-title"
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <MapIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                {t("sites.allSitesMapTitle")}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseAllSitesDialog}
              sx={{
                color: "inherit",
                "&:hover": {
                  bgcolor: alpha(theme.palette.background.paper, 0.2),
                },
              }}
              aria-label={t("sites.closeAllSitesAria")}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "calc(100vh - 64px)" }}>
            {loadError ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography color="error" variant="h6">
                  {t("sites.errorLoadMap")}
                </Typography>
              </Box>
            ) : mapsLoaded ? (
              sites && sites.length > 0 ? (
                <GoogleMap
                  defaultCenter={center}
                  style={{ width: "100%", height: "100%" }}
                  defaultZoom={zoom || 10}
                  mapId="YOUR_MAP_ID"
                  zoomControl={true}
                  mapTypeControl={true}
                  streetViewControl={true}
                  fullscreenControl={false}
                  gestureHandling="greedy"
                  minZoom={3}
                  maxZoom={20}
                  onLoad={(map) => {
                    setMapInstance(map);
                  }}
                  aria-label={t("sites.allSitesMapAria")}
                >
                  {sites.map((site) =>
                    Number.isFinite(site.latitude) &&
                    Number.isFinite(site.longitude) &&
                    site.latitude !== 0 &&
                    site.longitude !== 0 ? (
                      <AdvancedMarker
                        key={site.id}
                        position={{
                          lat: site.latitude,
                          lng: site.longitude,
                        }}
                        title={site.name}
                        onClick={() => handleMarkerClick(site.id)}
                      >
                        <LocationOnIcon
                          style={{
                            fontSize: site.parent_id ? 32 : 48,
                            color: site.parent_id ? "#1976d2" : "#d32f2f",
                            cursor: "pointer",
                            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                          }}
                        />
                        {selectedMarkerId === site.id && (
                          <InfoWindow
                            position={{
                              lat: site.latitude,
                              lng: site.longitude,
                            }}
                            onCloseClick={handleInfoWindowClose}
                          >
                            <Box sx={{ p: 1, minWidth: 200 }}>
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{ color: "primary.main", mb: 1 }}
                              >
                                {site.name}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>{t("sites.type")}:</strong>{" "}
                                {site.parent_id
                                  ? t("sites.subsite")
                                  : t("sites.parentSite")}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>{t("sites.coordinates")}:</strong>{" "}
                                {site.latitude.toFixed(6)},{" "}
                                {site.longitude.toFixed(6)}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>{t("sites.created")}:</strong>{" "}
                                {convertUTCToLocal(site.created_at)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>{t("sites.updated")}:</strong>{" "}
                                {convertUTCToLocal(site.updated_at)}
                              </Typography>
                            </Box>
                          </InfoWindow>
                        )}
                      </AdvancedMarker>
                    ) : null
                  )}
                </GoogleMap>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    {t("sites.noSitesMap")}
                  </Typography>
                </Box>
              )
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress
                  size={60}
                  aria-label={t("sites.loadingMapAria")}
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <NewDeleteModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleDelete}
          itemType={t("sites.itemType")}
        />

        {/* Sites Guide Dialog */}
        <SitesGuideDialog
          open={helpModalOpen}
          onClose={() => setHelpModalOpen(false)}
        />

        {/* Toast Notification */}
        <Toast
          open={toast.open}
          onClose={handleToastClose}
          severity={toast.severity}
          message={toast.message}
        />
      </Box>
    </APIProvider>
  );
}

export default Sites;
